import agent from 'src/agent';

export type Economics = {
    contribution?: string | number | null;
    conversion?: string | number | null;
    other?: string | number | null;
};
export type Profile = {
    description?: string;
    website?: string;
    website_notes?: string;
    knowledge_notes?: string;
    region?: string;
    category_id?: number | null;
    criteria?: string;
    exclusions?: string;
    processing_goal?: string;
    recommendation_url?: string;
    brand_accent?: string;
    brand_source?: 'website' | 'preset' | 'custom';
    brand_confirmed?: boolean;
    selection?: Selection;
};
export type Stream = {
    id: number;
    name: string;
    category_name: string;
    category_id: number;
    balance: number;
    status: string;
    is_active: boolean;
    access_expires_at?: string | null;
    access_name?: string | null;
};
export type Workspace = {
    id: number;
    project_id: number;
    name: string;
    profile: Profile;
    economics: Economics;
    revision: number;
    streams: Stream[];
};
export type Message = { role: 'user' | 'assistant'; content: string };
export type Conversation = { id: string; title: string; messages?: Message[]; updated_at?: string };
export type Source = {
    id: string;
    name: string;
    kind: string;
    status: 'connected' | 'no_recent_data' | 'not_connected';
    sources: number;
};
export type Capabilities = {
    checkout: boolean;
    auto_topup: boolean;
    gifts: boolean;
    referral_reward: string;
    minimum_budget: string;
    sources: Source[];
};
export type Package = { size: number; price_per_lead: string; total: string };
export type Category = { id: number; name: string; available: boolean; packages: Package[] };
export type Catalog = {
    categories: Category[];
    processing: { id: number; title: string; dialogs: number; total: string }[];
    sources: Source[];
};
export type Selection = { package_size?: number | null; budget?: string | null; agent_package_id?: number | null };
export type Quote = {
    id: string;
    expires_at: string;
    leads: number;
    price_per_lead: string;
    lead_cost: string;
    processing_cost: string;
    dialogs: number;
    budget: string;
    remainder: string;
    category_name: string;
    demand: { category_requests_30d: number; note: string };
    profile_revision: number;
};
export type Order = {
    id: string;
    state: string;
    kind: string;
    amount: string;
    wallet_used: string;
    snapshot: Partial<Quote> & { auto_not_enabled?: boolean };
    confirmation_url: string;
    created_at: string;
    gift_url?: string;
    gift_activated_at?: string | null;
    detail?: string;
};
export type Lead = {
    id: number;
    text: string;
    source: string;
    url: string | null;
    date: string;
    reason: string;
    rules: string[];
    stage: string;
    revenue: string | null;
    note: string;
};
export type Report = {
    period: string;
    delivered: number;
    results_known: number;
    won: number;
    revenue: string | null;
    revenue_complete: boolean;
    lead_purchases: string;
    consumed_lead_cost: string | null;
    cost_basis_complete: boolean;
    processing_cost: string | null;
    processing_cost_note: string;
    stages: Record<string, number>;
};
export type Renewal = {
    enabled: boolean;
    threshold: number;
    monthly_limit: string | null;
    last_error: string;
    pending_payments: number;
};
export type Processing = {
    agents: { id: number; name: string; status: string }[];
    conversations: { id: number; status: string; sales_stage: string; sales_outcome: string }[];
};

const root = '/intent';
async function get<T>(path: string): Promise<T> {
    return (await agent.get(root + path, { timeout: 18000 })).data;
}
async function post<T>(path: string, data: unknown): Promise<T> {
    return (await agent.post(root + path, data, { timeout: 35000 })).data;
}
const IntentService = {
    capabilities: () => get<Capabilities>('/capabilities/'),
    catalog: () => get<Catalog>('/catalog/'),
    list: () => get<{ workspaces: Workspace[]; wallet: string; dialogs: number | null }>('/workspaces/'),
    create: (name: string) => post<Workspace>('/workspaces/', { name }),
    website: (id: number, url: string) =>
        post<{ url: string; notes: string; brand_colors?: string[] }>('/workspaces/' + id + '/website/', { url }),
    save: async (workspace: Workspace) =>
        (
            await agent.put<Workspace>(root + '/workspaces/' + workspace.id + '/', {
                name: workspace.name,
                profile: workspace.profile,
                economics: workspace.economics,
                revision: workspace.revision,
            })
        ).data,
    claimDraft: (token: string) => post<Workspace>('/drafts/claim/', { token }),
    conversations: (id: number) => get<Conversation[]>('/workspaces/' + id + '/conversations/'),
    newConversation: (id: number) => post<Conversation>('/workspaces/' + id + '/conversations/', {}),
    conversation: (id: string) => get<Conversation>('/conversations/' + id + '/'),
    message: (id: string, message: string) =>
        post<Conversation & { suggestion?: Record<string, any> }>('/conversations/' + id + '/', { message }),
    quote: (id: number, selection: Selection) => post<Quote>('/workspaces/' + id + '/quote/', selection),
    orders: () => get<Order[]>('/orders/'),
    order: (id: string) => get<Order>('/orders/' + id + '/'),
    checkout: (data: unknown) => post<Order>('/orders/', data),
    refund: (id: string) => post<Order>('/orders/' + id + '/', { action: 'request_refund' }),
    renewal: (id: number) => get<Renewal>('/workspaces/' + id + '/renewal/'),
    disableRenewal: (id: number) => post<Renewal>('/workspaces/' + id + '/renewal/', { enabled: false }),
    search: (id: number, action: 'pause' | 'resume') => post<Workspace>('/workspaces/' + id + '/search/', { action }),
    leads: (id: number) => get<{ total: number; leads: Lead[] }>('/workspaces/' + id + '/leads/'),
    outcome: (id: number, lead: Lead) =>
        post('/workspaces/' + id + '/leads/' + lead.id + '/', {
            stage: lead.stage,
            revenue: lead.revenue,
            note: lead.note,
        }),
    results: (id: number) => get<Report>('/workspaces/' + id + '/results/'),
    processing: (id: number) => get<Processing>('/workspaces/' + id + '/processing/'),
    claimGift: (token: string) => post<{ activated: boolean; amount: string }>('/gifts/claim/', { token }),
    previewGift: (token: string) =>
        post<{ amount: string; message: string }>('/gifts/claim/', { token, preview: true }),
    referral: () =>
        get<{
            url: string;
            reward: string;
            invited: number;
            rewarded: number;
            history: { id: number; state: string; reward: string }[];
        }>('/referral/'),
    claimReferral: (token: string) => post('/referral/', { token }),
};
export default IntentService;

export function errorMessage(error: any): string {
    if (error?.response?.status === 404)
        return 'Новый рабочий кабинет ещё не подключён к серверу. Текущие потоки и лиды доступны в меню.';
    const data = error?.response?.data;
    if (typeof data === 'string' && !data.startsWith('<')) return data.slice(0, 300);
    if (typeof data?.detail === 'string') return data.detail;
    if (Array.isArray(data)) return data.join(' ');
    if (data && typeof data === 'object')
        return Object.entries(data)
            .map(([k, v]) => k + ': ' + (Array.isArray(v) ? v.join(' ') : String(v)))
            .join(' ')
            .slice(0, 350);
    return 'Не удалось выполнить действие. Проверьте соединение и повторите.';
}
